import { useEffect, useRef, useState } from "react";

/**
 * Tanren's process motif — a thin horizontal rule with small tick marks
 * between stages, thin grey numerals, and stage titles beneath.
 */
const smoothScrollTo = (id) => {
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history && typeof window.history.replaceState === "function") {
    window.history.replaceState(null, "", `#${id}`);
  }
};

export const ProcessLine = ({ steps, variant = "dark", compact = false, testId = "process-line" }) => {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const isDark = variant === "dark";
  const textColor = isDark ? "text-paper" : "text-char-900";
  const mutedColor = isDark ? "text-char-600" : "text-char-700";
  const numeralColor = isDark ? "text-char-600" : "text-char-700";

  const handleNodeClick = (href) => (e) => {
    if (!href || !href.startsWith("#")) return;
    e.preventDefault();
    smoothScrollTo(href.slice(1));
  };

  const wrapWithLink = (child, href, ariaLabel) => {
    if (!href) return child;
    return (
      <a
        href={href}
        onClick={handleNodeClick(href)}
        aria-label={ariaLabel}
        className="block outline-none focus-visible:ring-2 focus-visible:ring-ember focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
      >
        {child}
      </a>
    );
  };

  return (
    <div ref={wrapRef} data-testid={testId} className="w-full">
      {/* Desktop */}
      <div className="hidden md:block relative pt-8">
        <div
          className={`absolute top-0 left-0 right-0 overflow-hidden ${
            isDark ? "process-track process-track-dark" : "process-track"
          }`}
        >
          <div
            className="h-full origin-left"
            style={{
              background: isDark ? "rgba(250,249,247,0.5)" : "#0D0D0D",
              transform: visible ? "scaleX(1)" : "scaleX(0)",
              transition: "transform 1400ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          />
        </div>

        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}
        >
          {steps.map((s, i) => {
            const body = (
              <div className="relative pr-6 group">
                <span
                  aria-hidden="true"
                  className={`absolute -top-8 left-0 ${
                    isDark ? "process-tick process-tick-dark" : "process-tick"
                  }`}
                  style={{
                    opacity: visible ? 1 : 0,
                    transition: `opacity 500ms ease ${400 + i * 120}ms`,
                  }}
                />
                <div
                  className={`numeral text-3xl lg:text-4xl ${numeralColor} group-hover:text-ember transition-colors`}
                  data-testid={`${testId}-node-${s.number}`}
                >
                  {s.number}
                </div>
                {!compact ? (
                  <div className="mt-4">
                    <div className={`font-display text-lg ${textColor} tracking-tight leading-snug`}>
                      {s.title}
                    </div>
                    {s.description && (
                      <p className={`mt-2 text-sm ${mutedColor} leading-relaxed max-w-[26ch]`}>
                        {s.description}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className={`mt-3 text-sm ${mutedColor} leading-snug max-w-[18ch]`}>
                    {s.title}
                  </div>
                )}
              </div>
            );
            return (
              <div key={s.number}>{wrapWithLink(body, s.href, `Jump to ${s.title}`)}</div>
            );
          })}
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden flex flex-col">
        {steps.map((s) => {
          const body = (
            <div
              className={`flex items-baseline gap-5 py-4 border-t ${
                isDark ? "border-paper/15" : "border-char-900/10"
              }`}
            >
              <span className={`numeral text-2xl ${numeralColor}`}>{s.number}</span>
              <div>
                <div className={`font-display text-base ${textColor} tracking-tight`}>{s.title}</div>
                {s.description && !compact && (
                  <p className={`mt-1.5 text-sm ${mutedColor}`}>{s.description}</p>
                )}
              </div>
            </div>
          );
          return <div key={s.number}>{wrapWithLink(body, s.href, `Jump to ${s.title}`)}</div>;
        })}
      </div>
    </div>
  );
};

export default ProcessLine;
